import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Settings, Edit3, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminConstants';

const AdminPlansTab = () => {
  const { getToken } = useAuth();
  const [planConfigs, setPlanConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/admin/plan-configs`, { headers: { Authorization: `Bearer ${token}` } });
      setPlanConfigs(res.data);
    } catch { toast.error('Erro ao carregar planos'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const savePlanConfig = async (planId, data) => {
    setSaving(true);
    try {
      const token = getToken();
      await axios.put(`${API}/admin/plan-configs/${planId}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Plano atualizado!');
      setEditingPlan(null);
      fetchPlans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao atualizar plano'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="planos-section">
      <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Settings className="w-5 h-5 text-emerald-500" />Configuracao de Planos</CardTitle>
          <CardDescription>Edite os nomes e valores dos planos. As alteracoes refletem no checkout imediatamente. Use <b>Na vitrine / Oculto</b> para definir quais planos aparecem para cadastros novos (quem já está no plano continua normal).</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div> : (
            <div className="space-y-3">
              {planConfigs.map(p => (
                <div key={p.plan_id} className={`border rounded-xl p-4 transition-colors ${editingPlan === p.plan_id ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`plan-${p.plan_id}`}>
                  {editingPlan === p.plan_id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div><label className="text-xs font-medium text-slate-600 mb-1 block">Nome do Plano</label><input defaultValue={p.name} id={`plan-name-${p.plan_id}`} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
                        <div><label className="text-xs font-medium text-slate-600 mb-1 block">Preço (R$)</label><input type="number" step="0.01" defaultValue={p.price} id={`plan-price-${p.plan_id}`} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
                        <div><label className="text-xs font-medium text-slate-600 mb-1 block">Limite Avaliacoes</label><input type="number" defaultValue={p.limit || ''} placeholder="Ilimitado" id={`plan-limit-${p.plan_id}`} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => { const n=document.getElementById(`plan-name-${p.plan_id}`).value; const pr=document.getElementById(`plan-price-${p.plan_id}`).value; const l=document.getElementById(`plan-limit-${p.plan_id}`).value; savePlanConfig(p.plan_id, {name:n,price:parseFloat(pr),limit:l?parseInt(l):null}); }} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-4" data-testid={`save-plan-${p.plan_id}`}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Salvar'}</Button>
                        <Button variant="outline" onClick={() => setEditingPlan(null)} className="text-xs h-8 px-4">Cancelar</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-bold text-xs">{p.plan_id === 'free' ? 'F' : p.plan_id === 'basico' ? 'B' : p.plan_id === 'pro' ? 'P' : p.plan_id === 'enterprise' ? 'E' : p.plan_id === 'elite' ? 'EL' : '5'}</div>
                        <div><p className="font-bold text-slate-900">{p.name}</p><p className="text-xs text-slate-500">ID: {p.plan_id} | Limite: {p.limit ? `${p.limit}/mes` : 'Ilimitado'}</p></div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-xl font-bold text-emerald-600">{p.price > 0 ? `R$ ${p.price.toFixed(2)}` : 'Grátis'}</p>
                        <button
                          onClick={() => savePlanConfig(p.plan_id, { visivel_na_vitrine: !(p.visivel_na_vitrine !== false) })}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${p.visivel_na_vitrine !== false ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                          title={p.visivel_na_vitrine !== false ? 'Na vitrine: aparece no site, /feira e paineis. Clique para ocultar' : 'Oculto da vitrine: ninguém novo pode escolher. Quem já está nele segue sendo cobrado normalmente'}
                          data-testid={`shelf-toggle-${p.plan_id}`}
                        >
                          {p.visivel_na_vitrine !== false ? <><Eye className="w-3.5 h-3.5" />Na vitrine</> : <><EyeOff className="w-3.5 h-3.5" />Oculto</>}
                        </button>
                        <button onClick={() => setEditingPlan(p.plan_id)} className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors" data-testid={`edit-plan-${p.plan_id}`}><Edit3 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPlansTab;
